import _get from 'lodash-es/get';
import _set from 'lodash-es/set';
import _trim from 'lodash-es/trim';

import type { FilesFileSchemaWithId } from '@iconik-io/iconik-api/src/services/files';

import {
  UPLOADER_PERMISSIONS_COLLECTION,
  UPLOADER_PERMISSIONS_DEFAULT,
} from './constants';
import type {
  DirectoriesTree,
  PermissionParams,
  UploaderCollectionPermissions,
  UploaderPathItem,
  UploaderTopLevelPermissions,
} from './types';

export const getUploadErrorMessage = (
  entityName: string,
  fileExists: boolean,
): string => {
  let message = `Failed to upload ${entityName}`;
  if (fileExists) {
    message =
      'Failed uploading. A file already exists with the same name in this storage. Please rename the file in the upload dialog.';
  }
  return message;
};

export const getAssetDirectoryPath = (
  assetId: string,
  storageId: string,
  assetFiles: FilesFileSchemaWithId[],
): string | undefined => {
  const targetStorageFile = assetFiles.find(
    (assetFile) =>
      assetFile.asset_id === assetId && assetFile.storage_id === storageId,
  );
  return targetStorageFile?.directory_path;
};

export function getPathParts(file: UploaderPathItem): string[] {
  const fullpath = file.fullpath ?? '';
  const parts = fullpath.replace(/^\//, '').split('/');
  return parts;
}

export const getPathFromParts = (parts: string[]) => {
  const parents = [...parts];
  parents.pop();
  return parents.join('/');
};

export function trimPath(path: string): string {
  return _trim(path, '/');
}

export function combineFilePathsIntoTree(
  files: UploaderPathItem[],
): DirectoriesTree {
  const tree = {};
  files.forEach((file) => {
    const parts = getPathParts(file);
    parts.pop();
    if (!_get(tree, parts)) {
      _set(tree, parts, true);
    }
  });
  return tree;
}

type TraverseTreeCallback = (key: string, path: string) => void;

export const traverseTree = async (
  obj: DirectoriesTree,
  callback: TraverseTreeCallback,
  path = '',
): Promise<void> => {
  const keys = Object.keys(obj);
  for (const k of keys) {
    await callback(k, path);
    const objElement = obj[k];
    if (objElement && typeof objElement === 'object') {
      await traverseTree(objElement, callback, path ? `${path}/${k}` : k);
    }
  }
};

export const getFileCollectionPath = (
  collectionPath: string,
  file: UploaderPathItem,
  reflectDirectories: boolean,
): string => {
  const fullPath = getPathFromParts(getPathParts(file));
  return reflectDirectories
    ? `${collectionPath}${fullPath ? '/' + fullPath : ''}`
    : collectionPath;
};

export const getPermissionsParams = (
  collectionPermissions: UploaderCollectionPermissions,
  topLevelPermissions: UploaderTopLevelPermissions,
  collectionId?: string,
  shareId?: string,
): PermissionParams => {
  const params: PermissionParams = {};
  if (shareId) {
    return {
      assign_to_collection: true,
    };
  }
  if (collectionId) {
    params.apply_default_acls =
      collectionPermissions === UPLOADER_PERMISSIONS_DEFAULT;
    params.apply_collection_acls =
      collectionPermissions === UPLOADER_PERMISSIONS_COLLECTION;
  } else {
    params.apply_default_acls =
      topLevelPermissions === UPLOADER_PERMISSIONS_DEFAULT;
  }
  return params;
};
